import React, { useEffect, useMemo, useState } from 'react';
import { Badge, DataTable, RowActions, type DataTableColumn } from '@/components/ui';
import { ForemanArea } from '../../../services/areasService';
import { useClientSort } from '../../../utils/useClientSort';
import ServerPagination from '../../../components/ServerPagination';
import { countLabel } from '../utils/areaFilters';
import { AreaCountLink, ServiceAreaParentLink } from './AreaRelationLink';

interface ForemanAreasTableProps {
  foremanAreas: ForemanArea[];
  onEdit: (foremanArea: ForemanArea) => void;
  onDelete: (id: number) => void;
  canEdit: boolean;
  canDelete: boolean;
  /** Open Contract Areas filtered to this foreman area (W-M22). */
  onShowContractAreas?: (foremanArea: ForemanArea) => void;
  /** Open Service Areas filtered to this parent (W-M22). */
  onShowServiceArea?: (serviceAreaName: string) => void;
  emptyMessage?: string;
}

const SORT_VALUES: Record<string, (fa: ForemanArea) => string | number | null | undefined> = {
  name: (fa) => fa.name,
  serviceArea: (fa) => fa.serviceArea?.name,
  contractAreas: (fa) => fa.contractAreas?.length || 0,
};

const ForemanAreasTable: React.FC<ForemanAreasTableProps> = ({
  foremanAreas,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  onShowContractAreas,
  onShowServiceArea,
  emptyMessage = 'No foreman areas found.',
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { sorted, sortBy, sortDir, onSort } = useClientSort(foremanAreas, SORT_VALUES);
  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    onSort(key, dir);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [foremanAreas]);

  const total = foremanAreas.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  if (foremanAreas.length === 0) {
    return (
      <div className="tw-text-center tw-py-4">
        <p className="tw-text-content-muted">{emptyMessage}</p>
      </div>
    );
  }

  const foremanAreaColumns: DataTableColumn<ForemanArea>[] = [
    { key: 'name', header: 'Name', sortable: true, cell: (fa) => <strong>{fa.name}</strong>, mobile: 'title' },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      mobileLabel: 'Service Area',
      cell: (fa) => (
        <ServiceAreaParentLink serviceArea={fa.serviceArea} onOpen={(name) => onShowServiceArea?.(name)} />
      ),
    },
    {
      key: 'contractAreas',
      header: 'Contract Areas',
      sortable: true,
      mobileLabel: 'Contract Areas',
      cell: (fa) => {
        const n = fa.contractAreas?.length || 0;
        return (
          <AreaCountLink
            count={n}
            label={countLabel(n, 'contract area')}
            onOpen={() => onShowContractAreas?.(fa)}
            title={`Show the contract areas in ${fa.name}`}
          />
        );
      },
    },
    {
      key: 'mapInfo',
      header: 'Map Info',
      mobileLabel: 'Map Info',
      cell: (fa) => (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <small className="tw-text-content-muted">
            {typeof fa.latitude === 'number' && typeof fa.longitude === 'number'
              ? `${fa.latitude.toFixed(5)}, ${fa.longitude.toFixed(5)}`
              : 'No coordinates'}
          </small>
          <small className="tw-text-content-muted">{fa.address ? 'Has address' : 'No address'}</small>
          {fa.omitFromWordpressMap === true && <Badge bg="dark">Hidden from map and communities list</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      hidden: !(canEdit || canDelete),
      cell: (fa) => {
        const hasChildren = !!fa.contractAreas && fa.contractAreas.length > 0;
        return (
          <RowActions
            actions={[
              {
                kind: 'edit',
                label: 'Edit Foreman Area',
                onClick: () => onEdit(fa),
                hidden: !canEdit,
              },
              {
                kind: 'delete',
                // The disabled reason rides in the label — a kebab item has no hover target.
                label: hasChildren ? 'Delete Foreman Area – has contract areas' : 'Delete Foreman Area',
                onClick: () => onDelete(fa.id),
                disabled: hasChildren,
                hidden: !canDelete,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <DataTable
        columns={foremanAreaColumns}
        data={pageSlice}
        rowKey={(fa) => fa.id}
        striped
        bordered
        hover
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <ServerPagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={total}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        className="tw-mt-3"
      />
    </div>
  );
};

export default ForemanAreasTable;
